import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import * as moment from 'moment';

// Import dialog components
import { StoreOperationHoursDialogComponent } from '../../../../store-creation/store-operation-hours/store-operation-hours.component';
import { EditOperationHoursComponent } from '../../../../store-creation/edit-operation-hours/edit-operation-hours.component';
import { AddSpecialHoursDialogComponent } from '../../../../store-creation/special-hours/add-dialog/add-special-hours-dialog.component';
import { EditSpecialHoursDialogComponent } from '../../../../store-creation/special-hours/edit-dialog/edit-special-hours-dialog.component';
import { PreparationHoursComponent } from '../../../../store-creation/preparation-hours/preparation-hours.component';

// Import interfaces from the store creation component
import {
	TimeRange,
	OperatingHoursEntry,
	specialHoursEntry,
	StoreOperationHourDto,
} from '../../../../store-creation/partner-mart-create-store-multistep.component';
import { StoreService } from 'app/services';
import { Utils } from 'app/utils';

export interface GetStoreHoursRequest {
	StoreId: number;
	TimezoneOffset: number;
}

export interface GetStoreHoursResponse {
	Items: StoreOperationHourDto[];
}

// Special Hours interfaces
export interface GetStoreSpecialHoursRequest {
	StoreId: number;
	TimezoneOffset: number;
}

export interface GetStoreSpecialHoursResponse {
	Items: StoreSpecialHourDto[];
}

export interface StoreSpecialHourDto {
	Id: number;
	Title: string;
	StartDate: string;
	EndDate: string;
	IsOpen: boolean;
	StoreSpecialHourTimes: StoreSpecialHourTimeDto[];
}

export interface StoreSpecialHourTimeDto {
	Id: number;
	OpensAt: string;
	ClosesAt: string;
}

export interface AddStoreSpecialHourRequest {
	StoreId: number;
	SpecialHour: {
		Title: string;
		StartDate: string;
		EndDate: string;
		IsOpen: boolean;
		StoreSpecialHourTimes: {
			OpensAt: string;
			ClosesAt: string;
		}[];
	};
	TimezoneOffset: number;
}

export interface UpdateStoreSpecialHourRequest {
	StoreId: number;
	SpecialHourId: number;
	SpecialHour: {
		Title: string;
		StartDate: string;
		EndDate: string;
		IsOpen: boolean;
		StoreSpecialHourTimes: {
			OpensAt: string;
			ClosesAt: string;
		}[];
	};
}

export interface DeleteStoreSpecialHourRequest {
	StoreId: number;
	SpecialHourId: number;
}

export interface AddStoreOperationHourRequest {
	StoreId: number;
	TimezoneOffset: number;
	OperationHour: {
		DayOfWeek: number;
		StartTime: string;
		EndTime: string;
	};
}



@Component({
	selector: 'store-hours-details',
	standalone: false,
	templateUrl: './store-hours-details.component.html',
	styleUrl: './store-hours-details.component.scss',
})
export class StoreHoursDetailsComponent implements OnInit {
	@Input() storeId: number | any;
	@Input() storeData: any; // Add input to receive store data from parent

	savedOperationHours: OperatingHoursEntry[] = [];
	saveSpecialHours: specialHoursEntry[] = [];
	prepTimeValue: string = '20 Minutes';
	prepTimeValueInt: number = 20;
	activeSection: string = 'operation'; // Default to operation hours section

	constructor(
		private dialog: MatDialog,
		private messageService: MessageService,
		private storeService: StoreService
	) {}

	ngOnInit(): void {
		this.getStoreHours();
		this.getStoreSpecialHours();
		this.setPreparationTimeFromStoreData();
	}

	setActiveSection(section: string): void {
		this.activeSection = section;
	}

	setPreparationTimeFromStoreData(): void {
		if (this.storeData && this.storeData.PreperationTime) {
			this.prepTimeValueInt = this.storeData.PreperationTime;
			this.prepTimeValue = `${this.storeData.PreperationTime} Minutes`;
		}
	}

	showModal(): void {
		const dialogRef = this.dialog.open(StoreOperationHoursDialogComponent, {
			width: '900px',
			panelClass: 'storeTimeDialog',
			data: {
				savedHours: [], // Initialize with empty array for new entries
				savedOperationHours: this.savedOperationHours,
				existingOperationHours: this.savedOperationHours,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				// Handle the new data structure with both time ranges and selected days
				if (result.timeRanges && result.selectedDays) {
					// Create a new operating hours entry
					const newEntry: OperatingHoursEntry = {
						selectedDays: result.selectedDays,
						timeRanges: result.timeRanges,
					};

					// Save to backend for each time range and day combination
					this.saveOperationHoursToBackend(newEntry);
				} else {
					// Fallback for old data structure
					const newEntry: OperatingHoursEntry = {
						selectedDays: [],
						timeRanges: result,
					};
					// Save to backend
					this.saveOperationHoursToBackend(newEntry);
				}
			}
		});
	}

	private saveOperationHoursToBackend(entry: OperatingHoursEntry): void {
		// Reuse the addNewOperationHoursToBackend method to avoid code duplication
		this.addNewOperationHoursToBackend(entry);
	}

	private updateOperationHoursToBackend(updatedEntry: OperatingHoursEntry, originalEntry: OperatingHoursEntry): void {
		// Get all operation hour IDs to delete (either from new array or old single ID)
		const operationHourIds = originalEntry.operationHourIds || (originalEntry.operationHourId ? [originalEntry.operationHourId] : []);

		if (operationHourIds.length > 0) {
			// Delete all existing operation hours for the original days
			const deletePromises = operationHourIds.map(operationHourId =>
				this.storeService.deleteStoreOperationHour({ StoreId: this.storeId, OperationHourId: operationHourId }, this.messageService)
			);

			Promise.all(deletePromises)
				.then(() => {
					// After successful deletion, add the new operation hours
					this.addNewOperationHoursToBackend(updatedEntry);
				})
				.catch((error) => {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to update operating hours. Please try again.',
					});
				});
		} else {
			// If no operationHourId exists, just add the new hours
			this.addNewOperationHoursToBackend(updatedEntry);
		}
	}

	private addNewOperationHoursToBackend(entry: OperatingHoursEntry): void {
		// For each selected day and time range combination, add to backend
		entry.selectedDays.forEach(dayName => {
			entry.timeRanges.forEach(timeRange => {
				const request: AddStoreOperationHourRequest = {
					StoreId: this.storeId,
					TimezoneOffset: Utils.getTimezoneOffset(),
					OperationHour: {
						DayOfWeek: this.getDayOfWeekNumber(dayName),
						StartTime: moment(this.convertTimeRangeToDateTime(timeRange, 'start')).format('YYYY-MM-DDTHH:mm:ss'),
						EndTime: moment(this.convertTimeRangeToDateTime(timeRange, 'end')).format('YYYY-MM-DDTHH:mm:ss'),
					}
				};

				this.storeService
					.addStoreOperationHour(request, this.messageService)
					.then(() => {
						// Refresh data from backend after successful save
						this.getStoreHours();

						this.messageService.add({
							severity: 'success',
							summary: 'Success',
							detail: 'Operating hours updated successfully!',
						});
					})
					.catch((error) => {
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: 'Failed to update operating hours. Please try again.',
						});
					});
			});
		});
	}

	editOperationHours(entryIndex: number): void {
		const entry = this.savedOperationHours[entryIndex];

		// Get existing operation hours excluding the current entry being edited
		const existingOperationHours = this.savedOperationHours.filter(
			(_, index) => index !== entryIndex
		);

		const dialogRef = this.dialog.open(EditOperationHoursComponent, {
			width: '900px',
			panelClass: 'storeTimeDialog',
			data: {
				savedOperationHours: entry.timeRanges,
				savedSelectedDays: entry.selectedDays,
				existingOperationHours,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				// Handle the new data structure with both time ranges and selected days
				if (result.timeRanges && result.selectedDays) {
					// Update the specific entry
					const updatedEntry: OperatingHoursEntry = {
						selectedDays: result.selectedDays,
						timeRanges: result.timeRanges,
					};

					// Save Edited Operation Hours to Backend
					this.updateOperationHoursToBackend(updatedEntry, entry);

					// Update the entry in the array
					this.savedOperationHours[entryIndex] = updatedEntry;
				}
			}
		});
	}

	removeOperationHours(entryIndex: number): void {
		if (this.savedOperationHours.length > 1) {
			// Get the item to delete before removing it from the array
			const toRemove = this.savedOperationHours[entryIndex];

			// Immediately remove from local array for better UX
			this.savedOperationHours.splice(entryIndex, 1);

			// Get all operation hour IDs to delete (either from new array or old single ID)
			const operationHourIds = toRemove.operationHourIds || (toRemove.operationHourId ? [toRemove.operationHourId] : []);

			if (operationHourIds.length > 0) {
				// Delete all operation hour IDs from backend
				const deletePromises = operationHourIds.map(operationHourId =>
					this.storeService.deleteStoreOperationHour({StoreId: this.storeId, OperationHourId: operationHourId}, this.messageService)
				);

				Promise.all(deletePromises)
					.then(() => {
						// Refresh data from backend to ensure consistency
						this.getStoreHours();
						this.messageService.add({
							severity: 'success',
							summary: 'Success',
							detail: 'Operation hours deleted successfully!',
						});
					})
					.catch((error) => {
						// If backend deletion fails, restore the item to the array
						this.savedOperationHours.splice(entryIndex, 0, toRemove);
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: 'Failed to delete operation hours. Please try again.',
						});
					});
			} else {
				// If no operationHourId exists, just refresh the data
				this.getStoreHours();
				this.messageService.add({
					severity: 'warn',
					summary: 'Warning',
					detail: 'No operation hours found to delete.',
				});
			}
		}
	}

	openAddSpecialHoursDialog() {
		// Extract existing special hours date ranges to pass to dialog
		const existingDateRanges = this.saveSpecialHours.map(specialHour => ({
			startDate: specialHour.startDate,
			endDate: specialHour.endDate
		}));

		const ref = this.dialog.open(AddSpecialHoursDialogComponent, {
			width: '1000px',
			data: {
				existingSpecialHours: existingDateRanges
			}
		});

		ref.afterClosed().subscribe((result) => {
			if (result) {
				// Convert frontend data to backend format
				const specialHourRequest: AddStoreSpecialHourRequest = {
					StoreId: this.storeId,
					TimezoneOffset: Utils.getTimezoneOffset(),
					SpecialHour: {
						Title: result.description,
						StartDate: result.startDate.toISOString(),
						EndDate: result.endDate.toISOString(),
						IsOpen: result.availability,
						StoreSpecialHourTimes: result.timeRanges.map(
							(timeRange: TimeRange) => ({
								OpensAt: moment(this.convertTimeRangeToDateTime(
									timeRange,
									'start'
								)).format('YYYY-MM-DDTHH:mm:ss'),
								ClosesAt: moment(this.convertTimeRangeToDateTime(
									timeRange,
									'end'
								)).format('YYYY-MM-DDTHH:mm:ss'),
							})
						),
					},
				};

				// Save to backend
				this.storeService
					.addStoreSpecialHour(specialHourRequest, this.messageService)
					.then(() => {
						// Refresh data from backend
						this.getStoreSpecialHours();

						this.messageService.add({
							severity: 'success',
							summary: 'Success',
							detail: 'Special hours added successfully!',
						});
					})
					.catch((error) => {
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: 'Failed to add special hours. Please try again.',
						});
					});
			}
		});
	}

	removeSpecialHours(index: number) {
		// Get the special hour to be removed
		const specialHourToRemove = this.saveSpecialHours[index];

		// Immediately remove from local array for better UX
		this.saveSpecialHours.splice(index, 1);

		// Call backend to delete the special hour
		if (specialHourToRemove.id) {
			const deleteRequest: DeleteStoreSpecialHourRequest = {
				StoreId: this.storeId,
				SpecialHourId: specialHourToRemove.id
			};

			this.storeService.deleteStoreSpecialHour(deleteRequest, this.messageService)
				.then(() => {
					// Refresh data from backend to ensure consistency
					this.getStoreSpecialHours();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'Special hours deleted successfully!',
					});
				})
				.catch((error) => {
					// If backend deletion fails, restore the item to the array
					this.saveSpecialHours.splice(index, 0, specialHourToRemove);
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to delete special hours. Please try again.',
						});
				});
		} else {
			// If no id exists, just refresh the data
			this.getStoreSpecialHours();
			this.messageService.add({
				severity: 'warn',
				summary: 'Warning',
				detail: 'No special hours found to delete.',
			});
		}
	}

	editSpecialHours(index: number) {
		const specialHourToEdit = this.saveSpecialHours[index];

		// Extract existing special hours date ranges excluding the current item being edited
		const existingDateRanges = this.saveSpecialHours
			.filter((_, i) => i !== index) // Exclude current item
			.map(specialHour => ({
				startDate: specialHour.startDate,
				endDate: specialHour.endDate
			}));

		const ref = this.dialog.open(EditSpecialHoursDialogComponent, {
			width: '1000px',
			data: {
				...specialHourToEdit,
				existingSpecialHours: existingDateRanges
			},
		});

		ref.afterClosed().subscribe((result) => {
			if (result) {
				// Update the entry in the array
				this.saveSpecialHours[index] = result;
			}
		});
	}

	private convertTimeRangeToDateTime(
		timeRange: TimeRange,
		type: 'start' | 'end'
	): Date {
		let hour: number;
		let minute: number;
		let period: string;

		if (type === 'start') {
			hour = timeRange.hour;
			minute = timeRange.minute;
			period = timeRange.period;
		} else {
			hour = timeRange.endHour;
			minute = timeRange.endMinute;
			period = timeRange.endPeriod;
		}

		// Convert 12-hour format to 24-hour format
		if (period === 'PM' && hour !== 12) {
			hour += 12;
		} else if (period === 'AM' && hour === 12) {
			hour = 0;
		}

		// Create a date with the specified time in local timezone
		const date = new Date();
		date.setHours(hour, minute, 0, 0);
		// Return a new Date object from the local timezone string
		return date;
	}

	onEditPreparationTime() {
		const dialogRef = this.dialog.open(PreparationHoursComponent, {
			width: '500px',
			data: {
				prepTimeValue: this.prepTimeValue,
				prepTimeValueInt: this.prepTimeValueInt
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.prepTimeValue = result.prepTimeValue;
				this.prepTimeValueInt = result.prepTimeValueInt;
				// Save the updated preparation time to the backend
				this.updatePreparationTime(result.prepTimeValueInt);
			}
		});
	}

	updatePreparationTime(prepTimeValueInt: number): void {
		// Create a minimal update request with only the required fields for updating preparation time
		const updateRequest = {
			Id: this.storeId,
			MerchantId: this.storeData?.MerchantId || 0,
			Name: this.storeData?.Name || '',
			Address: this.storeData?.Address || '',
			AddressDetails: this.storeData?.AddressDetails || '',
			PreperationTime: prepTimeValueInt,
			TimeZoneOffSet: Utils.getTimezoneOffset()
		};

		this.storeService.updateStore(updateRequest, this.messageService)
			.then(() => {
				// Update the store data if available
				if (this.storeData) {
					this.storeData.PreperationTime = prepTimeValueInt;
				}

				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: 'Preparation time updated successfully!',
				});
			})
			.catch((error) => {
				// Revert the local changes if backend update fails
				this.setPreparationTimeFromStoreData();

				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to update preparation time. Please try again.',
				});
			});
	}

	// Helper method to convert day name to day of week number
	getDayOfWeekNumber(dayName: string): number {
		const dayMap: { [key: string]: number } = {
			Sun: 0,
			Mon: 1,
			Tue: 2,
			Wed: 3,
			Thur: 4,
			Fri: 5,
			Sat: 6,
		};
		return dayMap[dayName] || 0;
	}

	// Helper method to format time range for display
	getFormattedTimeRange(entryIndex: number = 0): string {
		if (
			this.savedOperationHours.length === 0 ||
			!this.savedOperationHours[entryIndex]
		) {
			return 'No hours set';
		}

		// Get all time ranges from the specified entry
		const entry = this.savedOperationHours[entryIndex];
		if (entry.timeRanges.length === 0) {
			return 'No hours set';
		}

		// Format all time ranges
		const formattedRanges = entry.timeRanges.map((range) => {
			const startTime = this.formatTime(range.hour, range.minute, range.period);
			const endTime = this.formatTime(
				range.endHour,
				range.endMinute,
				range.endPeriod
			);
			return `${startTime} - ${endTime}`;
		});

		// Join multiple ranges with line breaks or commas
		return formattedRanges.join(', ');
	}

	formatTime(hour: number, minute: number, period: string): string {
		// The hour and period are already correctly set from createTimeRange
		// Just format them for display without recalculating the period
		let displayHour = hour;
		const displayPeriod = period;

		// Handle special case for 12 AM (hour = 0)
		if (hour === 0) {
			displayHour = 12;
		}

		const formattedHour = displayHour.toString();
		const formattedMinute = minute.toString().padStart(2, '0');
		return `${formattedHour}:${formattedMinute} ${displayPeriod}`;
	}

	formatSpecialHoursDateRange(startDate: Date, endDate: Date): string {
		if (!startDate || !endDate) {
			return '';
		}

		// Check if dates are the same
		if (startDate.getTime() === endDate.getTime()) {
			return this.formatDate(startDate);
		}

		// Format both dates
		return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
	}

	private formatDate(date: Date): string {
		const months = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];

		const month = months[date.getMonth()];
		const day = date.getDate().toString().padStart(2, '0');
		const year = date.getFullYear();

		return `${month} ${day}, ${year}`;
	}

	getStoreHours() {
		const request: GetStoreHoursRequest = {
			StoreId: this.storeId,
			TimezoneOffset: Utils.getTimezoneOffset(),
		};
		this.storeService
			.getStoreHours(request, this.messageService)
			.then((data: GetStoreHoursResponse) => {
				// Map StoreOperationHourDto[] to OperatingHoursEntry[]
				// Old requirements: PM, AM, Hrs, minute is separated
				this.savedOperationHours = this.mapStoreHoursToOperatingHours(
					data?.Items || []
				);
			})
			.catch((error) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to fetch store hours. Please try again.',
				});
				this.savedOperationHours = [];
			});
	}

	getStoreSpecialHours() {
		const request: GetStoreSpecialHoursRequest = {
			StoreId: this.storeId,
			TimezoneOffset: Utils.getTimezoneOffset(),
		};
		this.storeService
			.getStoreSpecialHours(request, this.messageService)
			.then((data: GetStoreSpecialHoursResponse) => {
				// Map StoreSpecialHourDto[] to specialHoursEntry[]
				this.saveSpecialHours = this.mapStoreSpecialHoursToFrontend(data.Items);
			});
	}

	private mapStoreSpecialHoursToFrontend(
		specialHours: StoreSpecialHourDto[]
	): specialHoursEntry[] {
		return specialHours.map((specialHour) => ({
			id: specialHour.Id, // Include the ID from backend
			availability: specialHour.IsOpen,
			timeRanges: specialHour.StoreSpecialHourTimes.map((time) =>
				this.createTimeRangeFromBackend(time)
			),
			open24hrs: false, // This would need to be determined from the data
			startDate: new Date(specialHour.StartDate),
			endDate: new Date(specialHour.EndDate),
			description: specialHour.Title,
		}));
	}

	private createTimeRangeFromBackend(time: any): TimeRange {
		// Parse the time strings from backend
		const opensAt = new Date(`2000-01-01T${time.OpensAt}`);
		const closesAt = new Date(`2000-01-01T${time.ClosesAt}`);

		// Convert to 12-hour format
		const start12Hour = this.convertTo12HourFormat(opensAt.getHours());
		const end12Hour = this.convertTo12HourFormat(closesAt.getHours());

		return {
			hour: start12Hour.hour,
			minute: opensAt.getMinutes(),
			period: start12Hour.period,
			endHour: end12Hour.hour,
			endMinute: closesAt.getMinutes(),
			endPeriod: end12Hour.period,
			start: opensAt,
			end: closesAt,
			startTime: this.formatTime12Hour(
				start12Hour.hour,
				opensAt.getMinutes(),
				start12Hour.period
			),
			endTime: this.formatTime12Hour(
				end12Hour.hour,
				closesAt.getMinutes(),
				end12Hour.period
			),
		};
	}

	private mapStoreHoursToOperatingHours(
		storeHours: StoreOperationHourDto[]
	): OperatingHoursEntry[] {
		const groupedByDay = this.groupStoreHoursByDay(storeHours);
		return this.convertToOperatingHours(groupedByDay);
	}

	private groupStoreHoursByDay(
		storeHours: StoreOperationHourDto[]
	): Map<number, StoreOperationHourDto[]> {
		const grouped = new Map<number, StoreOperationHourDto[]>();

		if (!storeHours || !Array.isArray(storeHours)) {
			return grouped;
		}

		storeHours.forEach((hour) => {
			const dayHours = grouped.get(hour.DayOfWeek) || [];
			dayHours.push(hour);
			grouped.set(hour.DayOfWeek, dayHours);
		});

		return grouped;
	}

	private convertToOperatingHours(
		groupedHours: Map<number, StoreOperationHourDto[]>
	): OperatingHoursEntry[] {
		const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];
		const operatingHours: OperatingHoursEntry[] = [];

		groupedHours.forEach((hours, dayOfWeek) => {
			const selectedDays = [dayNames[dayOfWeek]];
			const timeRanges = hours.map((hour) => this.createTimeRange(hour));

			// Store all operation hour IDs for this day to handle multiple time ranges
			const operationHourIds = hours.map(hour => hour.OperationHourId);

			operatingHours.push({ operationHourIds, selectedDays, timeRanges });
		});

		return operatingHours;
	}

	private createTimeRange(hour: StoreOperationHourDto): TimeRange {
		const startTime = this.parseTime(hour.StartTime);
		const endTime = this.parseTime(hour.EndTime);

		// Convert 24-hour format to 12-hour format for the edit dialog
		const start12Hour = this.convertTo12HourFormat(startTime.hour);
		const end12Hour = this.convertTo12HourFormat(endTime.hour);

		// Format the times properly for the edit dialog validation
		const formattedStartTime = this.formatTime12Hour(
			start12Hour.hour,
			startTime.minute,
			start12Hour.period
		);
		const formattedEndTime = this.formatTime12Hour(
			end12Hour.hour,
			endTime.minute,
			end12Hour.period
		);

		const timeRange = {
			hour: start12Hour.hour,
			minute: startTime.minute,
			period: start12Hour.period,
			endHour: end12Hour.hour,
			endMinute: endTime.minute,
			endPeriod: end12Hour.period,
			start: new Date(),
			end: new Date(),
			startTime: formattedStartTime,
			endTime: formattedEndTime,
		};

		return timeRange;
	}

	private parseTime(timeString: string): {
		hour: number;
		minute: number;
		period: 'AM' | 'PM';
	} {
		const { hour: rawHour, minute } = this.extractHourAndMinute(timeString);
		// For API responses, the time is already in 24-hour format
		// We need to extract the period from the 24-hour value
		const period = this.getPeriodFromHour(rawHour);

		return { hour: rawHour, minute, period };
	}

	private extractHourAndMinute(timeString: string): {
		hour: number;
		minute: number;
	} {
		const timeOnly = timeString.includes(' ')
			? timeString.split(' ')[0]
			: timeString;
		const [hourStr, minuteStr] = timeOnly.split(':');

		return {
			hour: parseInt(hourStr, 10),
			minute: parseInt(minuteStr, 10),
		};
	}

	private convertTo24Hour(hour: number, originalTimeString: string): number {
		// If already in 24-hour format (hour >= 13), return as-is
		if (hour >= 13) {
			return hour;
		}

		// Handle 12-hour format with AM/PM
		if (originalTimeString.includes(' ')) {
			const period = originalTimeString.split(' ')[1];

			if (period === 'PM' && hour !== 12) {
				return hour + 12;
			}
			if (period === 'AM' && hour === 12) {
				return 0;
			}
		}

		return hour;
	}

	private getPeriodFromHour(hour24: number): 'AM' | 'PM' {
		return hour24 < 12 ? 'AM' : 'PM';
	}

	// Convert 24-hour format to 12-hour format for edit dialog
	private convertTo12HourFormat(hour24: number): {
		hour: number;
		period: 'AM' | 'PM';
	} {
		let result: { hour: number; period: 'AM' | 'PM' };

		if (hour24 === 0) {
			result = { hour: 12, period: 'AM' };
		} else if (hour24 === 12) {
			result = { hour: 12, period: 'PM' };
		} else if (hour24 > 12) {
			result = { hour: hour24 - 12, period: 'PM' };
		} else {
			result = { hour: hour24, period: 'AM' };
		}

		return result;
	}

	// Format time in 12-hour format with leading zeros where needed (matching EditOperationHoursComponent format)
	private formatTime12Hour(
		hour: number,
		minute: number,
		period: 'AM' | 'PM'
	): string {
		if (!hour && hour !== 0) {
			return 'Input';
		}
		const paddedHour = hour.toString().padStart(2, '0');
		const paddedMinute = minute?.toString().padStart(2, '0') || '00';
		return `${paddedHour}:${paddedMinute} ${period}`;
	}
}
